import { default as cloneDeep } from 'lodash/cloneDeep';

import { Optional } from '@vimeo/core/types/generic';

import { QnAManager } from './QnAManager';
import { vimeoConfig } from '../../../../configs/VimeoConfig';
import {
  trackVenuesAddQaQuestion,
  trackVenuesAddQaQuestionReaction,
  trackVenuesAddQaReply,
  trackVenuesRemoveQaQuestionReaction,
} from '../../../../tracking/tracking_venues_engagement';
import { EComposerSessionType } from '../../../../types/composer_session';
import { ELiveErrorCode } from '../../../../types/error';
import { TFirebasePrescriber, TFirebaseReference } from '../../../../types/firebase';
import { IInteractionApplicationUser } from '../../../../types/interaction_room';
import {
  EQuestionState,
  IQnAQuestion,
  IQnAQuestionAuthor,
  IQnAQuestionRecord,
} from '../../../../types/interaction_tools';
import { postPageMessage } from '../../../../utils/embed/embed_messaging';
import { EInteractionEmbedSignal } from '../../../../utils/embed/embed_signals';
import { LiveError } from '../../../../utils/error';
import {
  batchedDatabaseUpdate,
  getRefChild,
  getRefValue,
  pushRefValue,
  removeRefValue,
  setRefValue,
  updateRefValue,
} from '../../../../utils/firebase/cruds';
import {
  createQuestionAuthorInteractionObject,
  createQuestionInteractionObject,
  createQuestionReplyInteractionObject,
  createQuestionSessionRecordInteractionObject,
} from '../../../../utils/firebase/interaction_objects';
import { queryInteractionUserSync } from '../../../../utils/store/interaction';
import { ELiveInteractionQuery, TLiveInteractionUserQueryResponse } from '../../../queries';
import { ELiveGraphicsSignal } from '../../../signals';

/**
 * Create dedicated QNA session.
 */
export async function createQnASession(
  manager: QnAManager,
  isQnAModerated: boolean,
): Promise<string> {
  manager.log.info('Creating qna session:', isQnAModerated);
  manager.assertIsInitialized();

  const sessionRef: TFirebaseReference = await pushRefValue(
    manager.firebase,
    manager.qnaSessionsRef,
  );
  const id: string = sessionRef.key as string;

  const qnaSession = createQuestionSessionRecordInteractionObject({
    id,
    isQnaModerated: isQnAModerated,
  });

  await setRefValue(manager.firebase, sessionRef, {
    ...qnaSession,
    createdAt: manager.firebase.database.serverTimestamp(),
  });

  return id;
}

/**
 * Toggle moderation state for whole set of RLEs.
 */
export async function toggleModerationState(
  manager: QnAManager,
  isQnAModerated: boolean,
): Promise<void> {
  manager.log.info('Toggling moderation state:', isQnAModerated);
  manager.assertIsInitialized();

  await setRefValue(manager.firebase, manager.qnaModerationStatusRef, isQnAModerated);
}

/**
 * Toggle anonymous questions state for whole set of RLEs.
 */
export async function toggleAnonymousQuestionsState(
  manager: QnAManager,
  isAnonymousQuestionsDisabled: boolean,
): Promise<void> {
  manager.log.info('Toggling anonymous questions state:', isAnonymousQuestionsDisabled);
  manager.assertIsInitialized();

  await setRefValue(
    manager.firebase,
    manager.qnaAnonymousQuestionsStatusRef,
    isAnonymousQuestionsDisabled,
  );
}

/**
 * Mark specific qna session as open and ready.
 */
export async function openQnASession(manager: QnAManager, sessionId: string): Promise<void> {
  manager.log.info('Open qna session:', sessionId);
  manager.assertIsInitialized();

  const sessionRef: TFirebaseReference = getRefChild(
    manager.firebase,
    manager.qnaSessionsRef,
    sessionId,
  );
  const session: Optional<IQnAQuestion> = await getRefValue(manager.firebase, sessionRef);

  if (session) {
    await batchedDatabaseUpdate(manager.firebase, manager.rootRef, [
      [manager.qnaActiveSessionMetaRef, sessionId],
      [
        getRefChild(manager.firebase, sessionRef, 'startedAt'),
        manager.firebase.database.serverTimestamp(),
      ],
    ]);
  } else {
    throw new LiveError('Tried to open session that does not exist.', {
      code: ELiveErrorCode.INVALID_PARAMETERS,
      data: {
        session,
        sessionId,
      },
    });
  }
}

/**
 * Close simple QNA session.
 */
export async function closeQnASession(manager: QnAManager): Promise<void> {
  manager.log.info('Closing current qna session');
  manager.assertIsInitialized();

  const { config } = manager.context;

  manager.emitSignal({ type: ELiveGraphicsSignal.QNA_QUESTION_DESELECTED });

  /**
   * Clear qna replies buffer value
   */
  manager?.qnaRepliesBuffer?.resetValue();

  /**
   * Reset moderation state if it is possible and required.
   */
  if (config.canUseQnaModeration) {
    await batchedDatabaseUpdate(manager.firebase, manager.rootRef, [
      [manager.qnaModerationStatusRef, false],
      [manager.qnaActiveSessionMetaRef, null],
    ]);
  } else {
    await removeRefValue(manager.firebase, manager.qnaActiveSessionMetaRef);
  }
}

/**
 * Approve question - move it form pending to active collection.
 */
export async function approveQuestion(manager: QnAManager, questionId: string): Promise<void> {
  manager.log.info('Approving question:', questionId);
  manager.assertIsInitialized();
  manager.assertActiveSessionExists();

  const pendingQuestionRef: TFirebaseReference = getRefChild(
    manager.firebase,
    manager.qnaActiveSessionRef,
    `pendingQuestions/${questionId}`,
  );
  const activeQuestionRef: TFirebaseReference = getRefChild(
    manager.firebase,
    manager.qnaActiveSessionRef,
    `questions/${questionId}`,
  );

  const question: IQnAQuestionRecord = await getRefValue(manager.firebase, pendingQuestionRef);

  question.state = EQuestionState.ASKED;

  /**
   * Do single action to move element from one collection to another.
   */
  await batchedDatabaseUpdate(manager.firebase, manager.rootRef, [
    [pendingQuestionRef, null],
    [activeQuestionRef, question],
  ]);
}

/**
 * Archive specific question and remove it from pending/active collections.
 */
export async function archiveQuestion(
  manager: QnAManager,
  questionId: string,
  isApproved: boolean,
): Promise<void> {
  manager.log.info('Archiving question:', questionId, isApproved);
  manager.assertActiveSessionExists();

  const { activeSessionQuestions, activeSessionPendingQuestions, activeSessionPinnedQuestionId } =
    manager.context;
  const question: IQnAQuestion = manager.assertQuestionExists(
    questionId,
    isApproved ? activeSessionQuestions : activeSessionPendingQuestions,
  );

  if (question.state === EQuestionState.ANSWERING) {
    manager.emitSignal({ type: ELiveGraphicsSignal.QNA_QUESTION_DESELECTED });
  }

  if (questionId === activeSessionPinnedQuestionId) {
    await unPinQuestion(manager, questionId);
  }

  await updateRefValue(
    manager.firebase,
    getRefChild(
      manager.firebase,
      manager.qnaActiveSessionRef,
      `${isApproved ? 'questions' : 'pendingQuestions'}/${questionId}`,
    ),
    {
      state: EQuestionState.ARCHIVED,
      archivedAt: manager.firebase.database.serverTimestamp(),
    },
  );
}

export async function unArchiveQuestion(
  manager: QnAManager,
  questionId: string,
  isApproved: boolean,
): Promise<void> {
  manager.log.info('Un-archiving question:', questionId, isApproved);
  manager.assertActiveSessionExists();

  if (isApproved) {
    await updateRefValue(
      manager.firebase,
      getRefChild(manager.firebase, manager.qnaActiveSessionRef, `questions/${questionId}`),
      {
        state: EQuestionState.ASKED,
        archivedAt: null,
      },
    );
  } else {
    return manager.approveQuestion(questionId);
  }
}

export async function deactivateQuestion(manager: QnAManager, questionId: string): Promise<void> {
  manager.log.info('Deactivating question:', questionId);
  manager.assertActiveSessionExists();

  manager.emitSignal({ type: ELiveGraphicsSignal.QNA_QUESTION_DESELECTED });

  await updateRefValue(
    manager.firebase,
    getRefChild(manager.firebase, manager.qnaActiveSessionRef, `questions/${questionId}`),
    {
      state: EQuestionState.ASKED,
    },
  );
}

/**
 * Start answering question and push graphic object to collection with descriptive snapshot.
 */
export async function answerQuestion(manager: QnAManager, questionId: string): Promise<void> {
  manager.log.info('Answering question:', questionId);
  manager.assertIsInitialized();
  manager.assertActiveSessionExists();

  const question: IQnAQuestion = manager.assertQuestionExists(questionId);
  const { activeSessionQuestions } = manager.context;

  /**
   * Mark all questions that are answering as asked already.
   * Mark selected question as answering.
   */
  const batchUpdates: Array<TFirebasePrescriber> = Object.entries(activeSessionQuestions)
    .filter(([, value]) => value.state === EQuestionState.ANSWERING)
    .map(([key]) => {
      return [
        getRefChild(
          manager.firebase,
          manager.qnaActiveSessionRef as TFirebaseReference,
          `questions/${key}/state`,
        ),
        EQuestionState.ASKED,
      ];
    });

  batchUpdates.push(
    [
      getRefChild(manager.firebase, manager.qnaActiveSessionRef, `questions/${questionId}/state`),
      EQuestionState.ANSWERING,
    ],
    [
      getRefChild(
        manager.firebase,
        manager.qnaActiveSessionRef,
        `questions/${questionId}/answered`,
      ),
      true,
    ],
  );

  /**
   * Emit global graphic changes.
   */
  manager.emitSignal({ type: ELiveGraphicsSignal.QNA_QUESTION_SELECTED, data: question });

  /**
   * Update all questions with single atomic request.
   */
  await batchedDatabaseUpdate(manager.firebase, manager.rootRef, batchUpdates);
}

/**
 * Submit new question.
 */
export async function submitQuestion(
  manager: QnAManager,
  question: string,
  isAnonymous: boolean,
): Promise<void> {
  manager.assertIsInitialized();
  manager.assertActiveSessionExists();

  /**
   * User logged in interaction tools is different from vimeo one.
   * You can be logged as broadcaster but become participant from guest page.
   */
  const { data: qnaUser }: TLiveInteractionUserQueryResponse = manager.queryDataSync({
    type: ELiveInteractionQuery.LIVE_INTERACTION_USER,
  })!;

  if (!qnaUser) {
    throw new LiveError('User should be authorized to submit questions.', {
      code: ELiveErrorCode.UNAUTHORIZED,
    });
  }

  const { isEventModerated, activeSessionId } = manager.context;
  const defaultAvatar = vimeoConfig.USER.DEFAULT_LOGO_URL(75);

  manager.log.info('Submitting question:', activeSessionId, isEventModerated);

  const targetRef: TFirebaseReference = await pushRefValue(
    manager.firebase,
    getRefChild(
      manager.firebase,
      manager.qnaActiveSessionRef,
      isEventModerated ? 'pendingQuestions' : 'questions',
    ),
    {},
  );

  const user: Optional<IQnAQuestionAuthor> = isAnonymous
    ? null
    : createQuestionAuthorInteractionObject({
        id: qnaUser.id.toString(),
        displayName: qnaUser.displayName,
        avatarUrl: isAnonymous ? defaultAvatar : (qnaUser.avatarUrl ?? defaultAvatar),
      });

  const questionRecord = createQuestionInteractionObject({
    anonymous: isAnonymous,
    id: targetRef.key as string,
    state: isEventModerated ? EQuestionState.PENDING : EQuestionState.ASKED,
    text: question,
    user,
  });

  await setRefValue(manager.firebase, targetRef, {
    ...questionRecord,
    createdAt: manager.firebase.database.serverTimestamp(),
  });

  if (manager.composerSessionType === EComposerSessionType.VENUE) {
    trackVenuesAddQaQuestion(activeSessionId as string, targetRef.key as string, isAnonymous);
  }

  // Track action.
  postPageMessage(EInteractionEmbedSignal.QUESTION_SUBMIT, cloneDeep(questionRecord), true);
}

/**
 * Submit vote for question.
 */
export async function voteQuestion(manager: QnAManager, questionId: string): Promise<void> {
  manager.assertIsInitialized();
  manager.assertActiveSessionExists();

  const interactionUser: Optional<IInteractionApplicationUser> = queryInteractionUserSync(manager);
  const userId: string = String(interactionUser?.id);

  if (!userId) {
    throw new LiveError('User should be authorized to vote questions.', {
      code: ELiveErrorCode.UNAUTHORIZED,
    });
  }

  const { activeSessionQuestions, activeSessionId } = manager.context;

  const isVotedByMe: boolean = Boolean(
    Object.values(activeSessionQuestions).find(
      (question: IQnAQuestion) => question.id === questionId,
    )?.isVotedByMe,
  );

  const isAnonymousQuestion: boolean = Boolean(
    Object.values(activeSessionQuestions).find(
      (question: IQnAQuestion) => question.id === questionId,
    )?.anonymous,
  );

  const targetRef: TFirebaseReference = getRefChild(
    manager.firebase,
    manager.qnaActiveSessionRef,
    `questions/${questionId}/votes/${userId}`,
  );

  if (isVotedByMe) {
    manager.log.info('Removing vote from question:', questionId);

    await removeRefValue(manager.firebase, targetRef);

    if (manager.composerSessionType === EComposerSessionType.VENUE) {
      trackVenuesRemoveQaQuestionReaction(
        String(activeSessionId),
        questionId,
        isAnonymousQuestion,
        'upvote',
      );
    }
  } else {
    manager.log.info('Voting for question:', questionId);

    await pushRefValue(manager.firebase, targetRef, {
      userId: userId,
    });

    if (manager.composerSessionType === EComposerSessionType.VENUE) {
      trackVenuesAddQaQuestionReaction(
        String(activeSessionId),
        questionId,
        isAnonymousQuestion,
        'upvote',
      );
    }
  }
}

/**
 * Submit reply for question.
 */
export async function submitQuestionReply(
  manager: QnAManager,
  questionId: string,
  questionReply: string,
): Promise<void> {
  manager.log.info('Replying question:', questionId);
  manager.assertIsInitialized();
  manager.assertActiveSessionExists();

  /**
   * User logged in interaction tools is different from vimeo one.
   * You can be logged as broadcaster but become participant from guest page.
   */
  const { data: qnaUser }: TLiveInteractionUserQueryResponse = manager.queryDataSync({
    type: ELiveInteractionQuery.LIVE_INTERACTION_USER,
  })!;

  if (!qnaUser) {
    throw new LiveError('User should be authorized to reply questions.', {
      code: ELiveErrorCode.UNAUTHORIZED,
    });
  }

  if (!qnaUser.isCreator && !qnaUser.capabilities.canUseQnaModeration) {
    throw new LiveError('User should be creator or moderator to reply questions.', {
      code: ELiveErrorCode.OPERATION_RESTRICTED,
    });
  }

  const { activeSessionQuestions, activeSessionId } = manager.context;

  const isAnonymousQuestion: boolean = Boolean(
    Object.values(activeSessionQuestions).find(
      (question: IQnAQuestion) => question.id === questionId,
    )?.anonymous,
  );

  manager.log.info('Submitting reply for question:', questionId, activeSessionId);

  const targetRef: TFirebaseReference = await pushRefValue(
    manager.firebase,
    getRefChild(
      manager.firebase,
      manager.qnaRepliesSessionsRef as TFirebaseReference,
      `${activeSessionId}/${questionId}`,
    ),
    {},
  );

  const questionReplyRecord = createQuestionReplyInteractionObject({
    questionId,
    text: questionReply,
    user: createQuestionAuthorInteractionObject({
      id: qnaUser.id.toString(),
      displayName: qnaUser.displayName,
      avatarUrl: qnaUser.avatarUrl ?? vimeoConfig.USER.DEFAULT_LOGO_URL(75),
    }),
  });

  await setRefValue(manager.firebase, targetRef, {
    ...questionReplyRecord,
    createdAt: manager.firebase.database.serverTimestamp(),
  });

  if (manager.composerSessionType === EComposerSessionType.VENUE) {
    trackVenuesAddQaReply(
      String(activeSessionId),
      questionId,
      isAnonymousQuestion,
      targetRef.key as string,
    );
  }
}

export async function deleteQuestionReply(
  manager: QnAManager,
  questionId: string,
  replyId: string,
): Promise<void> {
  manager.log.info('Deleting question reply:', replyId);

  if (!replyId || !questionId) {
    throw new LiveError('No question id or reply id supplied for removal.', {
      code: ELiveErrorCode.INVALID_PARAMETERS,
    });
  }

  manager.assertIsInitialized();
  manager.assertActiveSessionExists();
  manager.assertQuestionExists(questionId);

  /**
   * User logged in interaction tools is different from vimeo one.
   * You can be logged as broadcaster but become participant from guest page.
   */
  const { data: qnaUser }: TLiveInteractionUserQueryResponse = manager.queryDataSync({
    type: ELiveInteractionQuery.LIVE_INTERACTION_USER,
  })!;

  if (!qnaUser) {
    throw new LiveError('User should be authorized to delete questions replies.', {
      code: ELiveErrorCode.UNAUTHORIZED,
    });
  }

  if (!qnaUser.isCreator && !qnaUser.capabilities.canUseQnaModeration) {
    throw new LiveError('User should be creator or moderator to delete questions replies.', {
      code: ELiveErrorCode.OPERATION_RESTRICTED,
    });
  }

  const { activeSessionId } = manager.context;

  await removeRefValue(
    manager.firebase,
    getRefChild(
      manager.firebase,
      manager.qnaRepliesSessionsRef as TFirebaseReference,
      `${activeSessionId}/${questionId}/${replyId}`,
    ),
  );
}

export async function pinQuestion(manager: QnAManager, questionId: string): Promise<void> {
  manager.log.info('Pinning question:', questionId);
  manager.assertIsInitialized();
  manager.assertActiveSessionExists();

  const { activeSessionQuestions, activeSessionPinnedQuestionId } = manager.context;
  const question: IQnAQuestion = manager.assertQuestionExists(questionId, activeSessionQuestions);

  if (question.state === EQuestionState.PENDING || question.state === EQuestionState.ARCHIVED) {
    manager.log.warn(`Can not pin ${question.state} question.`);
    return;
  }

  if (questionId === activeSessionPinnedQuestionId) {
    manager.log.warn('Question is already pinned');
    return;
  }

  await setRefValue(
    manager.firebase,
    getRefChild(manager.firebase, manager.qnaActiveSessionRef, 'pinnedQuestionId'),
    questionId,
  );
}

export async function unPinQuestion(manager: QnAManager, questionId: string): Promise<void> {
  manager.log.info('Unpinning question:', questionId);
  manager.assertIsInitialized();
  manager.assertActiveSessionExists();

  const { activeSessionQuestions, activeSessionPinnedQuestionId } = manager.context;
  const question: IQnAQuestion = manager.assertQuestionExists(questionId, activeSessionQuestions);

  if (question.state === EQuestionState.PENDING || question.state === EQuestionState.ARCHIVED) {
    manager.log.warn(`Can not unpin ${question.state} question.`);
    return;
  }

  if (questionId !== activeSessionPinnedQuestionId) {
    manager.log.warn('Question is not pinned');
    return;
  }

  await removeRefValue(
    manager.firebase,
    getRefChild(manager.firebase, manager.qnaActiveSessionRef, 'pinnedQuestionId'),
  );
}

/**
 * Dispose manager and clean up all data.
 */
export function dispose(manager: QnAManager): void {
  manager.log.info('Disposing manager');

  manager.disposeSubscribers();

  manager.qnaSessionsRef = null;
  manager.qnaModerationStatusRef = null;
  manager.qnaActiveSessionRef = null;
  manager.qnaActiveSessionMetaRef = null;
  manager.qnaRepliesSessionsRef = null;
  manager.rootRef = null;

  manager.qnaRepliesBuffer = null;

  manager.setContext({
    config: {
      canUseQnaModeration: null,
      canViewQnaModeration: null,
    },
    qnaSessionsHistory: [],
    activeSessionId: null,
    activeSession: null,
    activeSessionQuestions: {},
    activeSessionPendingQuestions: {},
    isEventModerated: null,
    qnaReplies: {},
  });
}

/**
 * Unsubscribe from active qna session.
 */
export function disposeActiveSessionSubscription(manager: QnAManager): void {
  if (manager.qnaActiveSessionRef) {
    manager.log.info('Unsubscribing from qna session:', manager.qnaActiveSessionRef.toString());
    manager.qnaActiveSessionRef = null;
    manager.activeSessionUnSubscribers.forEach((it) => it());
    manager.activeSessionUnSubscribers.splice(0);

    manager.setContext({
      activeSessionId: null,
      activeSession: null,
      activeSessionQuestions: {},
      activeSessionPendingQuestions: {},
    });
  }
}
